import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "../../ui/card";
import type {
  ProPeopleListRender,
  ProPersonSummary,
} from "../../../lib/pro-agent-types";
import { personRoute } from "../../../lib/routes";

// list_people read widget (Person Model Completion M3 — collapses the
// retired pair of separate roster cards into ONE, since there is one kind of
// record: a person). It renders the REAL
// crm.Service.ListPeople rows the tool returned (capped to the first 10, and
// already ordered by attention — server ranks, model explains), so the pro
// sees exactly what's in the book instead of a model-narrated list that
// editorializes or fabricates rows. Read-only, no raw dollar figures, no
// capabilities (D9 — the copilot has no buttons).

const SILL_STATUS_LABEL: Record<string, string> = {
  not_invited: "Not invited",
  invited: "Invited",
  joined_sill: "Joined Sill",
  connected: "Connected",
};

function statusLabel(status: string): string {
  return SILL_STATUS_LABEL[status] ?? status;
}

function PersonRow({ p }: { p: ProPersonSummary }) {
  const meta = [statusLabel(p.sill_status), p.band].filter(Boolean).join(" · ");
  return (
    <Link
      to={personRoute(p.contact_id, "/people")}
      data-testid="people-card-row"
      className="flex items-center justify-between gap-3 py-1.5 hover:bg-muted/40"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-fg">
          {p.name || "Person"}
        </p>
        {meta ? (
          <p className="truncate text-xs text-fg-muted">{meta}</p>
        ) : null}
      </div>
    </Link>
  );
}

export function PeopleListCard({ card }: { card: ProPeopleListRender }) {
  const people = card.people ?? [];

  return (
    <Card data-testid="people-card">
      <CardHeader className="gap-1 pb-2">
        <div className="flex items-center gap-1.5">
          <Users size={13} aria-hidden className="text-fg-muted" />
          <p className="text-sm font-semibold text-fg">
            People
            {card.total > 0 ? (
              <span className="ml-1 font-normal text-fg-muted">
                ({card.total})
              </span>
            ) : null}
          </p>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {people.length === 0 ? (
          <p
            data-testid="people-card-empty"
            className="border-t border-border-soft py-3 text-sm text-fg-muted"
          >
            No people yet.
          </p>
        ) : (
          <div className="divide-y divide-border-soft border-t border-border-soft">
            {people.map((p) => (
              <PersonRow key={p.contact_id} p={p} />
            ))}
          </div>
        )}
      </CardContent>

      {card.truncated ? (
        <CardFooter className="border-t border-border-soft py-2.5">
          <Link
            to="/people"
            data-testid="people-card-viewall"
            className="inline-flex items-center gap-1 text-xs font-semibold text-pro-ink hover:underline"
          >
            Showing {people.length} of {card.total} · View all people
          </Link>
        </CardFooter>
      ) : null}
    </Card>
  );
}
